/**
 * FAWorkspaceOverview — operating dashboard for a single Focus Area.
 *
 * Sections, top to bottom - "what happened" first, then the numbers, then
 * the things you come here to EDIT (reordered 2026-10-01, owner ask):
 *   1. Header (FA name + counts + time-range pills)
 *   2. Hero strip (4 brand-tinted left-border tiles)
 *   3. Recent activity - the feed (#1220)
 *   4. Digest chips (last-7-day)
 *   5. Pipeline pulse (5 cells + blockers row)
 *   6. Bucket mix
 *   7. Throughput (tiles with a real number only)
 *   8. Team           - folded
 *   9. Tags           - folded
 *  10. Skills catalog - folded
 *
 * Data fetched via useFocusAreaWorkspace(code, timeRange).
 */

import { useMemo, useState } from 'react'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import { makeStyles, mergeClasses } from '@fluentui/react-components'
import { useUnreviewedLandings } from '../hooks/useUnreviewedLandings'
import { useDemands } from '../hooks/useDemands'
import { useFocusAreaWorkspace } from '../hooks/useFocusAreas'
import { activatable } from '../lib/activatable'
import { stageLabel } from '../lib/labels'
import { ActivityFeed } from './ActivityFeed'
import type {
  BucketMixKey,
  FocusAreaWorkspaceData,
  WorkspaceTimeRange,
} from '../types/focus_area'
import { semanticSuccess, semanticWarning, semanticDanger, semanticInfo } from '../theme'
import { calculateWorkspaceHeroMetrics } from '../lib/faWorkspaceMetrics'
import { FaTagManager } from './FaTagManager'
import { FaSkillCatalogManager } from './FaSkillCatalogManager'
import { bayerColors, bucketColors } from '../theme'

const TIME_RANGE_LABELS: Record<WorkspaceTimeRange, string> = {
  quarter: 'This quarter',
  year: 'Past year',
  all: 'All time',
}

// Labels come from the canonical map — an inline literal here is how the
// "Parking" / "Parking Lot" drift got in (see lib/labels.ts, issue #772).
const PULSE_STAGES: Array<{ key: string; label: string }> = [
  'scoping',
  'triaging',
  'resourcing',
  'closed',
  'parking',
].map((key) => ({ key, label: stageLabel(key) }))

/**
 * Bucket Mix display order + colors — issue #710 / mockup §6 (~L6280).
 *
 * Canonical bucket palette, shared with DemandList + DemandKanban — see
 * `frontend/src/index.css` `--bucket-*` CSS vars. Do not switch to Fluent
 * semantic colors; the literal hex is the source of truth.
 */
const BUCKET_DEFS: Array<{ key: BucketMixKey; label: string; color: string }> = [
  { key: 'major-unlocks', label: 'Major Unlocks', color: bucketColors.majorUnlocks },
  { key: 'high-priority', label: 'High Priority', color: bucketColors.highPriority },
  { key: 'quick-wins', label: 'Quick Wins', color: bucketColors.quickWins },
  { key: 'parking-lot', label: 'Parking Lot', color: bucketColors.parkingLot },
  // 'rejected' — the DERIVED vocabulary (#1156): the backend buckets on
  // effective_priority_bucket now, and 'declined' was a retired stored slug.
  { key: 'rejected', label: 'Rejected', color: bucketColors.declined },
]

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  /* header */
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: 0,
  },
  meta: {
    color: '#616161',
    fontSize: '13px',
    marginTop: '4px',
  },
  trGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  trLabel: {
    color: '#616161',
    fontSize: '12px',
    marginRight: '2px',
  },
  trBtn: {
    border: '1px solid #d1d1d1',
    background: '#FFFFFF',
    color: '#424242',
    fontSize: '12px',
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: '14px',
    cursor: 'pointer',
    ':hover': {
      background: bayerColors.navyLight,
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
    },
  },
  trBtnActive: {
    background: bayerColors.navy,
    color: '#FFFFFF',
    borderTopColor: bayerColors.navy,
    borderRightColor: bayerColors.navy,
    borderBottomColor: bayerColors.navy,
    borderLeftColor: bayerColors.navy,
    ':hover': {
      background: bayerColors.navy,
      borderTopColor: bayerColors.navy,
      borderRightColor: bayerColors.navy,
      borderBottomColor: bayerColors.navy,
      borderLeftColor: bayerColors.navy,
    },
  },
  /* hero strip */
  hero: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '12px',
    '@media (max-width: 800px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  heroTile: {
    background: '#FFFFFF',
    border: '1px solid #e6e6e6',
    borderLeftWidth: '4px',
    borderRadius: '6px',
    padding: '14px 16px',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    transition: 'box-shadow 120ms',
    ':hover': {
      boxShadow: '0 2px 8px rgba(20,58,111,0.10)',
    },
  },
  heroDanger: { borderLeftColor: semanticDanger },
  heroWarning: { borderLeftColor: semanticWarning },
  heroInfo: { borderLeftColor: semanticInfo },
  heroNeutral: { borderLeftColor: '#9e9e9e' },
  heroNum: {
    fontSize: '28px',
    fontWeight: 700,
    color: bayerColors.navy,
    lineHeight: 1,
  },
  heroLbl: {
    fontSize: '13px',
    color: '#424242',
    fontWeight: 500,
  },
  heroSub: {
    fontSize: '11px',
    color: '#757575',
  },
  /* section card */
  section: {
    background: '#FFFFFF',
    border: '1px solid #e6e6e6',
    borderRadius: '6px',
    padding: '16px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: bayerColors.navy,
    margin: '0 0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionMeta: {
    fontSize: '11px',
    fontWeight: 400,
    color: '#757575',
    marginLeft: 'auto',
  },
  /* digest */
  digestRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  digestChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    border: '1px solid transparent',
  },
  toneInfo: {
    background: bayerColors.navyLight,
    color: '#1565c0',
    borderTopColor: '#cce3f5',
    borderRightColor: '#cce3f5',
    borderBottomColor: '#cce3f5',
    borderLeftColor: '#cce3f5',
  },
  toneSuccess: {
    background: '#e8f5e9',
    color: '#2e7d32',
    borderTopColor: '#c8e6c9',
    borderRightColor: '#c8e6c9',
    borderBottomColor: '#c8e6c9',
    borderLeftColor: '#c8e6c9',
  },
  toneWarning: {
    background: '#fff3e0',
    color: '#e65100',
    borderTopColor: '#ffe0b2',
    borderRightColor: '#ffe0b2',
    borderBottomColor: '#ffe0b2',
    borderLeftColor: '#ffe0b2',
  },
  toneDanger: {
    background: '#ffebee',
    color: '#c62828',
    borderTopColor: '#ffcdd2',
    borderRightColor: '#ffcdd2',
    borderBottomColor: '#ffcdd2',
    borderLeftColor: '#ffcdd2',
  },
  /* pulse */
  pulseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: '8px',
    '@media (max-width: 800px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  pulseCell: {
    background: '#fafafa',
    border: '1px solid #e6e6e6',
    borderRadius: '6px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
      background: '#FFFFFF',
    },
  },
  pulseLbl: {
    fontSize: '11px',
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: 600,
  },
  pulseNum: {
    fontSize: '24px',
    fontWeight: 700,
    color: bayerColors.navy,
    lineHeight: 1,
  },
  pulseBar: {
    height: '4px',
    background: '#e6e6e6',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  pulseFill: {
    height: '100%',
    background: bayerColors.navy,
    transition: 'width 200ms',
  },
  pulseSla: {
    fontSize: '11px',
    color: semanticDanger,
    fontWeight: 500,
  },
  pulseSlaHidden: {
    visibility: 'hidden',
  },
  blockers: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
  },
  blockersLbl: {
    fontSize: '12px',
    color: '#616161',
    fontWeight: 500,
  },
  blockerPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 10px',
    background: '#fff3e0',
    color: '#e65100',
    border: '1px solid #ffe0b2',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': {
      background: '#ffe0b2',
    },
  },
  /* bucket mix (issue #710) */
  bucketGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  bucketRow: {
    display: 'grid',
    gridTemplateColumns: '160px 1fr 60px',
    alignItems: 'center',
    gap: '12px',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '120px 1fr 48px',
      gap: '8px',
    },
  },
  bucketName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#212121',
    fontWeight: 500,
  },
  bucketDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  bucketBarTrack: {
    height: '10px',
    background: '#f0f0f0',
    borderRadius: '5px',
    overflow: 'hidden',
    position: 'relative',
  },
  bucketBarFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 200ms',
    minWidth: '2px',
  },
  bucketCount: {
    fontSize: '13px',
    color: '#212121',
    fontWeight: 600,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  bucketEmpty: {
    fontSize: '12px',
    color: '#9e9e9e',
    fontStyle: 'italic',
    padding: '8px 0',
  },
  /* team */
  teamRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 0',
    borderBottom: '1px solid #f0f0f0',
    ':last-child': { borderBottom: 'none' },
  },
  teamAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: bayerColors.navy,
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  teamName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#212121',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  teamYou: {
    fontSize: '10px',
    padding: '1px 6px',
    background: bayerColors.navy,
    color: '#FFFFFF',
    borderRadius: '8px',
    fontWeight: 600,
  },
  teamRole: {
    fontSize: '11px',
    color: '#616161',
  },
  foldToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    fontFamily: 'inherit',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },
  foldChevron: {
    fontSize: '11px',
    color: '#616161',
    transitionProperty: 'transform',
    transitionDuration: '120ms',
  },
  /* throughput */
  tputGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '8px',
    '@media (max-width: 800px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  tputTile: {
    background: '#fafafa',
    border: '1px solid #e6e6e6',
    borderRadius: '6px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tputVal: {
    fontSize: '22px',
    fontWeight: 700,
    color: bayerColors.navy,
    lineHeight: 1.1,
  },
  tputLbl: {
    fontSize: '11px',
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  tputDelta: {
    fontSize: '11px',
    fontWeight: 500,
  },
  tputGood: { color: semanticSuccess },
  tputFlat: { color: '#616161' },
  tputBad: { color: semanticDanger },
  /* decisions */
  decisionRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'start',
    gap: '8px',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    ':last-child': { borderBottom: 'none' },
  },
  decisionId: {
    fontSize: '13px',
    color: bayerColors.navyMid,
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    ':hover': { textDecoration: 'underline' },
  },
  decisionTitle: {
    fontSize: '12px',
    color: '#424242',
    marginTop: '2px',
    lineHeight: '1.3',
  },
  decisionAction: {
    fontSize: '12px',
    color: '#616161',
    fontStyle: 'italic',
  },
  decisionMeta: {
    fontSize: '11px',
    color: '#757575',
    whiteSpace: 'nowrap',
  },
  /* loading + error */
  loading: {
    padding: '32px',
    textAlign: 'center',
    color: '#616161',
  },
  errorBox: {
    background: '#ffebee',
    color: '#c62828',
    border: '1px solid #ffcdd2',
    borderRadius: '6px',
    padding: '12px 16px',
    fontSize: '13px',
  },
  empty: {
    fontSize: '12px',
    color: '#9e9e9e',
    fontStyle: 'italic',
    padding: '8px 0',
  },
})

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function toneClassFor(
  tone: 'info' | 'success' | 'warning' | 'danger',
  s: ReturnType<typeof useStyles>,
): string {
  switch (tone) {
    case 'info':
      return s.toneInfo
    case 'success':
      return s.toneSuccess
    case 'warning':
      return s.toneWarning
    case 'danger':
      return s.toneDanger
  }
}

function trendClassFor(
  trend: 'good' | 'flat' | 'bad',
  s: ReturnType<typeof useStyles>,
): string {
  if (trend === 'good') return s.tputGood
  if (trend === 'bad') return s.tputBad
  return s.tputFlat
}

export interface FAWorkspaceOverviewProps {
  code: string
  readOnly?: boolean
  /** Optional initial state override (test seam) */
  initialTimeRange?: WorkspaceTimeRange
  /** Optional inline data override — when provided, skips the query (test seam) */
  data?: FocusAreaWorkspaceData
}

/**
 * A section you come to EDIT, not to read: collapsed to one line with a count,
 * open on request. Team, Tags and the Skills catalog all live under it. The
 * overview is read for state, and a dozen names or eighty-eight catalog rows
 * are not state.
 */
function Fold({
  id,
  title,
  meta,
  children,
}: {
  id: string
  title: string
  meta?: string
  children: React.ReactNode
}) {
  const styles = useStyles()
  const [open, setOpen] = useState(false)
  return (
    <section className={styles.section} data-testid={`fa-${id}`}>
      <button
        type="button"
        className={styles.foldToggle}
        aria-expanded={open}
        aria-controls={`fa-${id}-list`}
        data-testid={`fa-${id}-toggle`}
        onClick={() => setOpen((o) => !o)}
      >
        <h3 className={styles.sectionTitle} style={{ margin: 0, flex: 1 }}>
          {title}
          {meta && <span className={styles.sectionMeta}>{meta}</span>}
        </h3>
        <span
          aria-hidden="true"
          className={styles.foldChevron}
          style={{ transform: open ? 'rotate(90deg)' : 'none' }}
        >
          {'\u25B6'}
        </span>
      </button>
      {open && (
        <div id={`fa-${id}-list`} style={{ marginTop: 8 }}>
          {children}
        </div>
      )}
    </section>
  )
}

export function FAWorkspaceOverview({
  code,
  initialTimeRange = 'quarter',
  data: dataOverride,
  readOnly = false,
}: FAWorkspaceOverviewProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const [timeRange, setTimeRange] = useState<WorkspaceTimeRange>(initialTimeRange)

  const query = useFocusAreaWorkspace(dataOverride ? undefined : code, timeRange)
  const heroQuery = useDemands({
    focus_area: code,
    page_size: 500,
    enabled: !dataOverride && !readOnly,
  })
  const unreviewedLandings = useUnreviewedLandings(code, !dataOverride && !readOnly)
  const data = dataOverride ?? query.data

  const heroMetrics = useMemo(() => {
    if (!heroQuery.data) return data?.hero ?? { overdue: 0, pending: 0, posted_no_apps: 0, stale_14d: 0 }
    return calculateWorkspaceHeroMetrics(heroQuery.data.items)
  }, [data?.hero, heroQuery.data])

  if (!dataOverride && query.isLoading) {
    return <div className={styles.loading}>Loading workspace…</div>
  }
  if (!dataOverride && query.isError) {
    return (
      <div className={styles.errorBox} role="alert">
        Could not load FA workspace.
      </div>
    )
  }
  if (!data) return null

  const pulseByKey = new Map(data.pulse.map((p) => [p.key, p]))
  const maxStage = Math.max(1, ...data.pulse.map((p) => p.count))
  const triageLink = `/triage?${new URLSearchParams({ focusArea: code }).toString()}`
  const overdueLink = `/triage?${new URLSearchParams({
    filter: 'overdue',
    focusArea: code,
  }).toString()}`
  const noApplicationsLink = `/demands?${new URLSearchParams({
    stage: 'resourcing',
    filter: 'no-applications',
    focusArea: code,
  }).toString()}`
  const staleLink = `/demands?${new URLSearchParams({
    stage: 'resourcing',
    filter: 'stale-14d',
    focusArea: code,
  }).toString()}`
  // The backend still ships "—" placeholders for the three unbuilt metrics; a
  // live page shows only the tiles with a number, or no card at all.
  const throughput = data.throughput.filter((t) => !t._placeholder)

  return (
    <div className={styles.root} data-testid="fa-workspace-overview">
      {/* HEADER */}
      <header className={styles.header} data-testid="fa-header">
        <div>
          <h1 className={styles.title}>{data.fa.code} Workspace</h1>
          <div className={styles.meta}>
            {data.fa.name} · Your operating view · {data.fa.demand_count} demand
            {data.fa.demand_count === 1 ? '' : 's'} · {data.fa.talent_count} talent
            {data.fa.talent_count === 1 ? '' : 's'} in FA
          </div>
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <div className={styles.trGroup} role="group" aria-label="Time range">
            <span className={styles.trLabel}>Time range:</span>
            {(['quarter', 'year', 'all'] as WorkspaceTimeRange[]).map((k) => (
              <button
                key={k}
                type="button"
                className={mergeClasses(styles.trBtn, timeRange === k && styles.trBtnActive)}
                aria-pressed={timeRange === k}
                onClick={() => setTimeRange(k)}
              >
                {TIME_RANGE_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* HERO STRIP */}
      {/* TODO(#710): hero labels diverge from mockup §6 — audit Tier 4 / intentional discussion.
            Mockup: Active demands / Overdue / New this period / Avg maturity
            v2:     Overdue decisions / Total waiting / Posted no apps / Posted >14d stale
            v2 labels are more actionable; do not flip without product sign-off. */}
      {!readOnly && <div className={styles.hero} data-testid="fa-hero">
        <button
          type="button"
          className={mergeClasses(styles.heroTile, styles.heroDanger)}
          onClick={() => navigate(overdueLink)}
        >
          <span className={styles.heroNum}>{heroMetrics.overdue}</span>
          <span className={styles.heroLbl}>Overdue decisions</span>
          <span className={styles.heroSub}>→ Go to triage</span>
        </button>
        <button
          type="button"
          className={mergeClasses(styles.heroTile, styles.heroWarning)}
          onClick={() => navigate(triageLink)}
        >
          <span className={styles.heroNum}>{heroMetrics.pending}</span>
          <span className={styles.heroLbl}>Total waiting on you</span>
          <span className={styles.heroSub}>→ Go to triage</span>
        </button>
        <button
          type="button"
          className={mergeClasses(styles.heroTile, styles.heroInfo)}
          onClick={() => navigate(noApplicationsLink)}
        >
          <span className={styles.heroNum}>{heroMetrics.posted_no_apps}</span>
          <span className={styles.heroLbl}>Posted, no applications yet</span>
          <span className={styles.heroSub}>→ Re-publicize or invite</span>
        </button>
        <button
          type="button"
          className={mergeClasses(styles.heroTile, styles.heroNeutral)}
          onClick={() => navigate(staleLink)}
        >
          <span className={styles.heroNum}>{heroMetrics.stale_14d}</span>
          <span className={styles.heroLbl}>Posted &gt; 14 days, stale</span>
          <span className={styles.heroSub}>→ Re-scope or close</span>
        </button>
      </div>}

      {/* New arrivals nobody has looked at (#1373). The hero above said "0
          waiting on you" while a landing waited: those tiles count the review
          band, and an arrival is not in it yet. Same number as the Audit queue
          nav badge; only when non-zero, like the strip below. */}
      {!readOnly && (unreviewedLandings.data ?? 0) > 0 && (
        <button
          type="button"
          data-testid="fa-unreviewed-landings"
          onClick={() => navigate('/audit-queue')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            marginRight: 8,
            borderRadius: 8,
            border: 'none',
            cursor: 'pointer',
            background: 'var(--blue-bg1, #e8f1fb)',
            color: 'var(--blue-fg1, #0f4c81)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {unreviewedLandings.data} new arrival{unreviewedLandings.data === 1 ? '' : 's'} waiting for
          a first look → Audit queue
        </button>
      )}

      {/* Unshepherded (#1208): the orphan state made visible where council
          plans. Only when non-zero — a clean FA earns a quiet page. */}
      {(data.hero.unshepherded ?? 0) > 0 && (
        <div
          data-testid="fa-unshepherded"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            borderRadius: 8,
            background: 'var(--yellow-bg1, #fff8e1)',
            color: 'var(--yellow-fg1, #8a6d00)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {data.hero.unshepherded} active demand{data.hero.unshepherded === 1 ? '' : 's'} with no
          caretaker — nobody is shepherding them
        </div>
      )}

      {/* RECENT ACTIVITY — the full feed (#1220), not just 5 stage moves */}
      {!readOnly && <section className={styles.section} data-testid="fa-decisions">
        <h3 className={styles.sectionTitle}>
          Recent activity
          <span className={styles.sectionMeta}>
            Stage moves, applications, invitations, intake and discussion in {code}
          </span>
        </h3>
        <ActivityFeed fa={code} />
      </section>}

      {/* DIGEST */}
      {data.digest.length > 0 && (
        <section className={styles.section} data-testid="fa-digest">
          <h3 className={styles.sectionTitle}>What changed this week</h3>
          <div className={styles.digestRow}>
            {data.digest.map((d) => (
              <span
                key={d.key}
                className={mergeClasses(styles.digestChip, toneClassFor(d.tone, styles))}
              >
                {d.label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* PULSE */}
      <section className={styles.section} data-testid="fa-pulse">
        <h3 className={styles.sectionTitle}>
          Pipeline pulse
          <span className={styles.sectionMeta}>{TIME_RANGE_LABELS[data.time_range]}</span>
        </h3>
        <div className={styles.pulseGrid}>
          {PULSE_STAGES.map((stage) => {
            const cell = pulseByKey.get(stage.key) ?? {
              key: stage.key,
              label: stage.label,
              count: 0,
              sla_breaches: 0,
            }
            const widthPct = (cell.count / maxStage) * 100
            return (
              <button
                key={stage.key}
                type="button"
                className={styles.pulseCell}
                // `focusArea` is not optional here: DemandList falls back to
                // the VIEWER's own FA when the param is absent, so opening
                // another focus area's workspace and clicking its pulse showed
                // your own demands under a different number. The hero tiles
                // above already pass it.
                onClick={() =>
                  navigate(`/demands?${new URLSearchParams({ stage: stage.key, focusArea: code })}`)
                }
                data-testid={`pulse-cell-${stage.key}`}
              >
                <div className={styles.pulseLbl}>{stage.label}</div>
                <div className={styles.pulseNum}>{cell.count}</div>
                <div className={styles.pulseBar}>
                  <div className={styles.pulseFill} style={{ width: `${widthPct}%` }} />
                </div>
                {cell.sla_breaches > 0 ? (
                  <div className={styles.pulseSla}>{cell.sla_breaches} past SLA</div>
                ) : (
                  <div className={mergeClasses(styles.pulseSla, styles.pulseSlaHidden)}>·</div>
                )}
              </button>
            )
          })}
        </div>
        {data.blockers.length > 0 && (
          <div className={styles.blockers} data-testid="fa-blockers">
            <span className={styles.blockersLbl}>Top blockers:</span>
            {data.blockers.map((b) => (
              <a
                key={b.id}
                className={styles.blockerPill}
                role="link"
                {...activatable(() => navigate(`/demand/${b.id}`))}
              >
                {b.demand_id} · {b.days_waiting}d waiting
              </a>
            ))}
          </div>
        )}
      </section>

      {/* BUCKET MIX (issue #710 / mockup §6 ~L6280) */}
      {data.bucket_mix && (() => {
        const mix = data.bucket_mix
        const unassigned = (data as any).bucket_unassigned ?? 0
        const assigned = BUCKET_DEFS.reduce((sum, b) => sum + (mix[b.key] ?? 0), 0)
        const total = assigned + unassigned
        return (
          <section className={styles.section} data-testid="fa-bucket-mix">
            <h3 className={styles.sectionTitle}>
              Priority Buckets
              <span className={styles.sectionMeta}>
                {total === 0
                  ? 'No active demands'
                  : `${total} active demand${total === 1 ? '' : 's'}`}
              </span>
            </h3>
            {total === 0 ? (
              <div className={styles.bucketEmpty}>
                No active demands to break down by priority bucket.
              </div>
            ) : (
              <div className={styles.bucketGrid}>
                {BUCKET_DEFS.map((b) => {
                  const count = mix[b.key] ?? 0
                  const widthPct = total > 0 ? (count / total) * 100 : 0
                  return (
                    <div
                      key={b.key}
                      className={styles.bucketRow}
                      data-testid={`bucket-row-${b.key}`}
                    >
                      <div className={styles.bucketName}>
                        <span
                          className={styles.bucketDot}
                          style={{ background: b.color }}
                          aria-hidden="true"
                        />
                        {b.label}
                      </div>
                      <div
                        className={styles.bucketBarTrack}
                        role="progressbar"
                        aria-label={`${b.label} share`}
                        aria-valuenow={Math.round(widthPct)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className={styles.bucketBarFill}
                          style={{ width: `${widthPct}%`, background: b.color }}
                        />
                      </div>
                      <div className={styles.bucketCount}>{count}</div>
                    </div>
                  )
                })}
                {unassigned > 0 && (
                  <div
                    className={styles.bucketRow}
                    data-testid="bucket-row-unassigned"
                  >
                    <div className={styles.bucketName}>
                      <span
                        className={styles.bucketDot}
                        style={{ background: '#9ca3af' }}
                        aria-hidden="true"
                      />
                      Unassigned
                    </div>
                    <div
                      className={styles.bucketBarTrack}
                      role="progressbar"
                      aria-label="Unassigned share"
                      aria-valuenow={Math.round((unassigned / total) * 100)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className={styles.bucketBarFill}
                        style={{ width: `${(unassigned / total) * 100}%`, background: '#9ca3af' }}
                      />
                    </div>
                    <div className={styles.bucketCount}>{unassigned}</div>
                  </div>
                )}
              </div>
            )}
          </section>
        )
      })()}


      {/* THROUGHPUT */}
      {throughput.length > 0 && (
        <section className={styles.section} data-testid="fa-throughput">
          <h3 className={styles.sectionTitle}>
            Throughput
            <span className={styles.sectionMeta}>{TIME_RANGE_LABELS[data.time_range]}</span>
          </h3>
          <div className={styles.tputGrid}>
            {throughput.map((t, idx) => (
              <div key={`${t.label}-${idx}`} className={styles.tputTile}>
                <div className={styles.tputVal}>{t.value}</div>
                <div className={styles.tputLbl}>{t.label}</div>
                <div
                  className={mergeClasses(styles.tputDelta, trendClassFor(t.trend, styles))}
                >
                  {t.delta}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TEAM | TAGS | SKILLS CATALOG - the three you come here to edit, folded. */}
      <Fold id="team" title="Team" meta={`${data.team.length} member${data.team.length === 1 ? '' : 's'}`}>
        {data.team.length === 0 ? (
          <div className={styles.empty}>No contributors yet.</div>
        ) : (
          data.team.map((m) => (
            <div key={m.id} className={styles.teamRow}>
              <div className={styles.teamAvatar}>{initials(m.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={styles.teamName}>
                  {m.name}
                  {m.is_you && <span className={styles.teamYou}>you</span>}
                </div>
                <div className={styles.teamRole}>{m.role}</div>
              </div>
            </div>
          ))
        )}
      </Fold>
      <Fold id="tags" title="Tags">
        <FaTagManager code={code} hideTitle />
      </Fold>
      <Fold id="skills-catalog" title="Skills catalog">
        <FaSkillCatalogManager code={code} hideTitle />
      </Fold>

    </div>
  )
}
